import { createContext, type ReactNode, use, useEffect, useMemo, useRef, useState } from "react";

const DEFAULT_DELAY_MS = 200;
const DEFAULT_MIN_DURATION_MS = 500;

interface LoadingIndicatorConfig {
    delayMs: number;
    minDurationMs: number;
}

const LoadingIndicatorContext = createContext<LoadingIndicatorConfig>({
    delayMs: DEFAULT_DELAY_MS,
    minDurationMs: DEFAULT_MIN_DURATION_MS
});

export interface LoadingIndicatorProviderProps {
    delayMs: number;
    minDurationMs: number;
    children: ReactNode;
}

export function LoadingIndicatorProvider({ delayMs, minDurationMs, children }: LoadingIndicatorProviderProps) {
    const value = useMemo(() => ({ delayMs, minDurationMs }), [delayMs, minDurationMs]);

    return <LoadingIndicatorContext value={value}>{children}</LoadingIndicatorContext>;
}

export interface UseLoadingIndicatorOptions {
    delayMs?: number;
    minDurationMs?: number;
}

export function useLoadingIndicator(isLoading: boolean, options: UseLoadingIndicatorOptions = {}) {
    const ctx = use(LoadingIndicatorContext);

    const { delayMs = ctx.delayMs, minDurationMs = ctx.minDurationMs } = options;

    const [shouldRender, setShouldRender] = useState(false);
    const shownAtRef = useRef<number | null>(null);

    if (isLoading && delayMs === 0 && !shouldRender) {
        setShouldRender(true);
    }

    useEffect(() => {
        if (isLoading) {
            if (shownAtRef.current !== null) {
                return;
            }

            if (delayMs === 0) {
                shownAtRef.current = performance.now();

                return;
            }

            const showTimer = setTimeout(() => {
                shownAtRef.current = performance.now();
                setShouldRender(true);
            }, delayMs);

            return () => clearTimeout(showTimer);
        }

        if (shownAtRef.current === null) {
            return;
        }

        const elapsed = performance.now() - shownAtRef.current;

        if (elapsed >= minDurationMs) {
            shownAtRef.current = null;
            setShouldRender(false);

            return;
        }

        const hideTimer = setTimeout(() => {
            shownAtRef.current = null;
            setShouldRender(false);
        }, minDurationMs - elapsed);

        return () => clearTimeout(hideTimer);
    }, [isLoading, delayMs, minDurationMs]);

    return shouldRender;
}
